import { Alert, Badge, Button, Stack, Text, TextInput, Textarea, ThemeIcon } from "@mantine/core"
import { useEffect, useState } from "react"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { ErrorAlert, PageHeader, SectionCard } from "atlas_forecasting_app/components/common/page_elements"
import { IconHeadset, IconShieldCheck, appIcon } from "atlas_forecasting_app/components/common/icons"

export function ContactPage({ currentUser }) {
  const [email, setEmail] = useState(currentUser?.email || "")
  const [subject, setSubject] = useState("")
  const [message, setMessage] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  useEffect(() => {
    if (!currentUser?.email) return
    setEmail((prev) => (prev ? prev : currentUser.email))
  }, [currentUser?.email])

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (submitting) return

    const trimmedEmail = email.trim()

    setSubmitting(true)
    try {
      await apiFetch("/api/contact_messages", {
        method: "POST",
        body: {
          email: trimmedEmail,
          subject,
          message,
        },
      })
      setEmail(trimmedEmail || currentUser?.email || "")
      setSubject("")
      setMessage("")
      setError("")
      setSuccessMessage("Your message has been saved. We’ll review it soon.")
    } catch (e) {
      setError(e.message)
      setSuccessMessage("")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow="Public support"
        variant="form"
        title="Contact Us"
        subtitle="Send a note to the Atlas Forecasting team and we’ll review it as soon as we can."
        action={<Badge variant="light">Public</Badge>}
      />

      <div className="af-form-layout">
        <SectionCard variant="form" title="Send a message" subtitle="Use this form for questions, feedback, or support requests.">
          <ErrorAlert message={error} />
          {successMessage ? (
            <Alert color="green" variant="light" title="Message received">
              {successMessage}
            </Alert>
          ) : null}

          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label="Email"
                type="email"
                required
                value={email}
                placeholder="you@example.com"
                onChange={(event) => setEmail(event.currentTarget.value)}
              />
              <TextInput
                label="Subject"
                required
                value={subject}
                placeholder="How can we help?"
                onChange={(event) => setSubject(event.currentTarget.value)}
              />
              <Textarea
                label="Message"
                required
                minRows={8}
                value={message}
                placeholder="What would you like to share?"
                onChange={(event) => setMessage(event.currentTarget.value)}
              />
              <Button type="submit" loading={submitting}>
                Send message
              </Button>
            </Stack>
          </form>
        </SectionCard>

        <SectionCard variant="utility" title="What to expect" subtitle="Atlas handles this inbox directly.">
          <Stack gap="md">
            <div className="af-info-row">
              <ThemeIcon variant="light" color="blue" radius="xl" size={40}>
                {appIcon(IconHeadset, "page")}
              </ThemeIcon>
              <div>
                <Text fw={700}>Share enough context</Text>
                <Text size="sm" c="dimmed">
                  Include what happened, what you expected, and what you’d like the Atlas team to help with.
                </Text>
              </div>
            </div>

            <div className="af-info-row">
              <ThemeIcon variant="light" color="blue" radius="xl" size={40}>
                {appIcon(IconShieldCheck, "page")}
              </ThemeIcon>
              <div>
                <Text fw={700}>Direct review</Text>
                <Text size="sm" c="dimmed">
                  Messages are saved in Atlas and reviewed by the team through the internal contact-message inbox.
                </Text>
              </div>
            </div>
          </Stack>
        </SectionCard>
      </div>
    </Stack>
  )
}
